/**
 * Calendar-day existence — the SINGLE isomorphic source of truth for "is this
 * Y/M/D triple a day that actually exists".
 *
 * WHY THIS IS SHARED, AND WHY IT IS SEPARATE FROM A RANGE CHECK
 *
 * Every date validator in the import pipeline bounded the day at 1–31 and the
 * month at 1–12. Those bounds accept `2025-02-30`, and JavaScript's `Date` then
 * ROLLS IT OVER to 2 March — a different, entirely plausible calendar day. No
 * error, no warning, and the wrong date looks normal forever after. Because
 * `transactions.date` drives FIFO lot ordering, tax-year assignment and the
 * long/short-term holding boundary, a silently shifted date is unrecoverable
 * once imported. The impossible-SHAPE case (`2025-13-45`) already fails loudly
 * (PR #1028); a nonexistent calendar DAY has to fail the same way.
 *
 * Frontend and backend both need it: the backend decides IMPORTABLE, the
 * frontend preview decides READABLE, and a preview that reformats `2025-02-30`
 * into `30/02/2025` presents as understood a cell the import will reject.
 */

/** Month lengths in a NON-leap year, indexed by month number (1–12). */
const DAYS_PER_MONTH_COMMON_YEAR: readonly number[] = [
  31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
]

/**
 * Gregorian leap rule: every 4th year, except centuries, except every 400th.
 * 2024 → true, 2023 → false, 1900 → false, 2000 → true.
 */
export function isGregorianLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

/** Number of days in `month` (1–12) of `year`. Returns 0 for a month outside 1–12. */
export function daysInCalendarMonth(year: number, month: number): number {
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return 0
  }
  if (month === 2 && isGregorianLeapYear(year)) {
    return 29
  }
  return DAYS_PER_MONTH_COMMON_YEAR[month - 1] ?? 0
}

/**
 * True when Y/M/D is a day that exists on the Gregorian calendar.
 *
 * `2024-02-29` → true (leap year), `2023-02-29` → false, `2025-02-30` → false,
 * `2025-04-31` → false, `2025-12-31` → true.
 */
export function isRealCalendarDay(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false
  }
  const maxDay = daysInCalendarMonth(year, month)
  return maxDay > 0 && day >= 1 && day <= maxDay
}

/**
 * A self-describing ISO calendar date at the START of the value —
 * `2025-02-30`, `2025-02-30 10:00:00`, `2025-02-30T10:00:00Z`. The ordering is
 * unambiguous (year first), so the day component can be checked without knowing
 * any template format.
 */
const LEADING_ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})(?![\d-])/

/**
 * A date whose month is SPELLED, in either order — `Feb 30 2025`,
 * `Feb 30, 2025`, `30 Feb 2025`. Also unambiguous: the spelled month leaves
 * only one reading for the two numbers, so the day can be checked without a
 * declared format. A purely NUMERIC triple (`05/03/2024`) is deliberately NOT
 * matched — its day/month ordering is unknowable here, and guessing it is the
 * separate bug `coerceCsvDateAfterFormatMismatchToIso` already refuses to make.
 */
const MONTH_NAME_DATE =
  /^(?:(\d{1,2})\s+)?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+(?:(\d{1,2})\b,?\s+)?(\d{4})\b/i

/** 3-letter English month abbreviations (lower case) → month number (1–12). */
export const MONTH_NAME_TO_NUMBER: Readonly<Record<string, number>> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
}

/**
 * True when the value spells out a calendar day that does not exist.
 *
 * For the parsers that hand a string straight to `new Date(...)`: that
 * constructor rolls a nonexistent day forward instead of failing (`2025-02-30`
 * → 2 March), so callers screen the string first and treat a `true` here as an
 * unparseable cell. A value in a shape this cannot read UNAMBIGUOUSLY returns
 * `false` — the predicate never guesses a day/month ordering.
 */
export function hasNonexistentCalendarDay(value: string): boolean {
  const trimmed = value.trim()

  const isoMatch = LEADING_ISO_CALENDAR_DATE.exec(trimmed)
  if (isoMatch !== null) {
    return !isRealCalendarDay(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]))
  }

  const namedMatch = MONTH_NAME_DATE.exec(trimmed)
  if (namedMatch !== null) {
    // Group 1 is the leading day (`30 Feb 2025`), group 3 the trailing one
    // (`Feb 30 2025`); exactly one is present in a real date.
    const dayText = namedMatch[1] ?? namedMatch[3]
    if (dayText === undefined) {
      return false
    }
    const month = MONTH_NAME_TO_NUMBER[namedMatch[2]?.toLowerCase() ?? '']
    if (month === undefined) {
      return false
    }
    return !isRealCalendarDay(Number(namedMatch[4]), month, Number(dayText))
  }

  return false
}
